import { ChangeDetectionStrategy, Component, computed, input, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { SelectModule } from 'primeng/select';

import { AssetEpisodeCardComponent } from '@features/assets/components/asset-episode-card/asset-episode-card.component';

import { IDetailsEpisode, IDetailsSeason } from '@features/details/data-access/details.models';

/** Episodes tab: season picker (p-select) + the episode list (reuses `asset-episode-card`). */
@Component({
    selector: 'details-series',
    standalone: true,
    imports: [FormsModule, SelectModule, AssetEpisodeCardComponent],
    templateUrl: './details-series.html',
    styleUrl: './details-series.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DetailsSeries {
    readonly seasons = input.required<IDetailsSeason[]>();
    readonly episodes = input.required<readonly IDetailsEpisode[]>();
    readonly selectedSeasonId = input<string | null>(null);
    readonly hasUpcomingEnabled = input(false);
    readonly hasOutgoingEnabled = input(false);

    readonly seasonChange = output<string>();

    // Bumped when an episode's availability flips, to re-pick which episode shows the auto badge.
    private readonly availabilityTick = signal(0);

    readonly latestAvailableEpisodeReferenceId = computed<string | null>(() => {
        this.availabilityTick();
        const now = Date.now();
        let latestReferenceId: string | null = null;
        let latestTime = -Infinity;
        let latestEpisodeNumber = -Infinity;
        for (const episode of this.episodes()) {
            if (!episode.availabilityStartTime) continue;
            const startTime = new Date(episode.availabilityStartTime).getTime();
            if (startTime > now) continue;
            const episodeNumber = episode.episodeNumber ?? -Infinity;
            if (startTime > latestTime || (startTime === latestTime && episodeNumber > latestEpisodeNumber)) {
                latestTime = startTime;
                latestEpisodeNumber = episodeNumber;
                latestReferenceId = episode.referenceId;
            }
        }
        return latestReferenceId;
    });

    onEpisodeAvailabilityChanged(): void {
        this.availabilityTick.update(v => v + 1);
    }
}
